import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { WarningCircle } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

/**
 * <Field> gives a form control its label, its hint and its error, and wires
 * them for assistive technology: the label points at the control, the hint
 * and the error are its description (aria-describedby), an error marks it
 * aria-invalid, `required` marks it aria-required (the control's own
 * `required` attribute, and the browser validation that comes with it, stay
 * the page's choice).
 *
 *   <Field label="Email" hint="Your confirmation goes here" error={errors.email} required>
 *     <Input type="email" name="email" autoComplete="email" />
 *   </Field>
 *
 * Input, Textarea, NativeSelect and the Select trigger read the field by
 * themselves; any other control (an autocomplete, a phone field) is wrapped
 * in <FieldControl> or calls useFieldControl(props).
 *
 * Label above, hint under the label, error under the control, with an icon:
 * the colour is never the only sign. The error is announced when it appears.
 */
const FieldContext = React.createContext(null)

const join = (...ids) => ids.filter(Boolean).join(" ") || undefined

/** The field around a control, or null outside a <Field>. */
export function useField() {
  return React.useContext(FieldContext)
}

/**
 * Merges the field's id, description, invalid, required and disabled states
 * into a control's props. What the control sets itself wins, except the
 * description, which is joined.
 */
export function useFieldControl(props) {
  const field = React.useContext(FieldContext)
  if (!field) return props
  const merged = {
    ...props,
    id: props.id ?? field.controlId,
    "aria-describedby": join(field.describedBy, props["aria-describedby"]),
  }
  if (props["aria-invalid"] === undefined && field.invalid) merged["aria-invalid"] = true
  if (props["aria-required"] === undefined && !props.required && field.required) merged["aria-required"] = true
  if (props.disabled === undefined && field.disabled) merged.disabled = true
  return merged
}

/** Wraps any other control so it reads the field (id, description, state). */
export const FieldControl = React.forwardRef((props, ref) => (
  <Slot ref={ref} {...useFieldControl(props)} />
))
FieldControl.displayName = "FieldControl"

/** The field's label. <Field label> renders one; compose your own (sr-only) when needed. */
export const FieldLabel = React.forwardRef(({ className, children, ...props }, ref) => {
  const field = useField()
  return (
    <label
      ref={ref}
      htmlFor={field?.controlId}
      className={cn(
        "text-label-md font-semibold text-touki-black group-data-[invalid]/field:text-touki-red",
        field?.disabled && "text-touki-muted",
        className
      )}
      {...props}>
      {children}
      {field?.required ? <span aria-hidden="true" className="ms-0.5 text-touki-muted">*</span> : null}
    </label>
  )
})
FieldLabel.displayName = "FieldLabel"

export const Field = React.forwardRef(({ id, label, hint, error, required = false, disabled, className, children, ...props }, ref) => {
  const auto = React.useId()
  const childId = React.isValidElement(children) ? children.props.id : undefined
  const controlId = id ?? childId ?? `${auto}control`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const invalid = Boolean(error)
  const value = React.useMemo(
    () => ({ controlId, describedBy: join(hintId, errorId), invalid, required, disabled }),
    [controlId, hintId, errorId, invalid, required, disabled]
  )
  return (
    <FieldContext.Provider value={value}>
      <div
        ref={ref}
        className={cn("group/field flex min-w-0 flex-col gap-1.5", className)}
        data-invalid={invalid ? "" : undefined}
        {...props}>
        {label != null && label !== false ? <FieldLabel>{label}</FieldLabel> : null}
        {hint ? <p id={hintId} className="text-label-md font-normal text-touki-muted">{hint}</p> : null}
        {children}
        {error ? (
          <p id={errorId} role="alert" className="flex items-start gap-1.5 text-label-md font-normal text-touki-red">
            <WarningCircle weight="fill" size={16} aria-hidden="true" focusable="false" className="mt-px shrink-0" />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
})
Field.displayName = "Field"
